/***************************************************************************
 * Shared form-adjuster and field-rule logic used by both `useRecordScreen`
 * (record-page edit mode) and `useRowEditor` (grid row edit mode).
 *
 * Extracted verbatim from `useRecordScreen`; behavior is intentionally
 * identical. Host-specific side effects (section visibility, widget reloads,
 * form-disable wiring) are injected as callbacks so the same module can
 * serve both hosts.
 ***************************************************************************/

import React from "react";
import {useFormikContext} from "formik";
import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import DynamicFormUtils, {DisabledFields, DynamicFormFieldDefinition} from "qqq/components/forms/DynamicFormUtils";
import {FieldRule, FieldRuleAction, FieldRuleTrigger} from "qqq/models/fields/FieldRules";
import Client from "qqq/utils/qqq/Client";

const qController = Client.getInstance();


/***************************************************************************
 * Backend response shape for form-adjuster endpoints. Matches the
 * `FormAdjusterOutput` DTO produced by `material-dashboard-backend`.
 ***************************************************************************/
export interface FormAdjusterOutput
{
   updatedFieldMetaData?: { [fieldName: string]: any };
   updatedFieldValues?: { [fieldName: string]: any };
   updatedFieldDisplayValues?: { [fieldName: string]: string };
   fieldsToClear?: string[];
   updatedSectionMetaData?: { [sectionName: string]: any };
   widgetsToReload?: string[];
   isFormDisabled?: boolean;
   formDisabledMessage?: string;
}


/***************************************************************************
 * Host-specific side-effect bundle. Callers inject their behavior; the
 * module stays pure over these interactions.
 ***************************************************************************/
export interface FormAdjusterCallbacks
{
   /** Apply updatedSectionMetaData to the host's section state. RecordScreen
    * implements this; row-editor passes a no-op. */
   applyUpdatedSections: (response: FormAdjusterOutput) => void;

   /** Called with the message when `isFormDisabled=true`. RecordScreen sets
    * a not-allowed error banner; row-editor shows the row banner. */
   onFormDisabled: (message: string) => void;

   /** Predicate — should this call actually honor `isFormDisabled`? RecordScreen
    * only honors it in edit/create mode; row-editor always honors it. */
   shouldApplyFormDisabled: () => boolean;

   /** Disable every editable field on the form. Host-specific because it
    * mutates host state (RecordScreen's `formFields`, row-editor's per-cell
    * disabled map). */
   disableAllFields: () => void;

   /** Refresh the named widgets in place (no remount). Optional — only the
    * post-save on-load path provides it; when absent, an adjuster's
    * `widgetsToReload` is ignored (that's what scopes the feature to post-save). */
   reloadWidgets?: (widgetNames: string[]) => void;
}


/***************************************************************************
 * Run the on-load form adjuster. Mutates `values` (in place) with
 * `updatedFieldValues`/`fieldsToClear`, mutates `table.fields` with
 * `updatedFieldMetaData`, and populates `defaultDisplayValues`.
 *
 * Side effects beyond those mutations are delegated to `callbacks`.
 *
 * `isStillCurrent`, when supplied, is checked after the network round-trip
 * resolves; if it returns false the response is dropped before any state
 * is touched.  Row-edit hosts use this to discard a stale adjuster reply
 * after the user cancelled or moved to a different row mid-flight.
 ***************************************************************************/
export async function runOnLoadFormAdjuster(
   table: QTableMetaData,
   values: { [key: string]: any },
   defaultDisplayValues: Map<string, string>,
   callbacks: FormAdjusterCallbacks,
   isStillCurrent?: () => boolean,
): Promise<void>
{
   const postBody = new FormData();
   postBody.append("event", "onLoad");
   postBody.append("allValues", JSON.stringify(values));
   const response: FormAdjusterOutput = await qController.axiosRequest(
      {
         method: "post",
         url: `/material-dashboard-backend/form-adjuster/table:${table.name}/onLoad`,
         data: postBody,
         headers: qController.defaultMultipartFormDataHeaders()
      });

   if (isStillCurrent && !isStillCurrent())
   {
      return;
   }

   const updatedFields = response.updatedFieldMetaData;
   if (updatedFields)
   {
      for (let updatedFieldName in updatedFields)
      {
         table.fields.set(updatedFieldName, new QFieldMetaData(updatedFields[updatedFieldName]));
      }
   }

   const updatedFieldValues = response?.updatedFieldValues ?? {};
   for (let fieldNameToUpdate in updatedFieldValues)
   {
      values[fieldNameToUpdate] = updatedFieldValues[fieldNameToUpdate];
   }

   const updatedFieldDisplayValues = response?.updatedFieldDisplayValues ?? {};
   for (let fieldNameToUpdate in updatedFieldDisplayValues)
   {
      defaultDisplayValues?.set(fieldNameToUpdate, updatedFieldDisplayValues[fieldNameToUpdate]);
   }

   const fieldsToClear = response?.fieldsToClear ?? [];
   for (let fieldToClear of fieldsToClear)
   {
      values[fieldToClear] = null;
   }

   callbacks.applyUpdatedSections(response);

   if (response?.widgetsToReload?.length && callbacks.reloadWidgets)
   {
      callbacks.reloadWidgets(response.widgetsToReload);
   }

   if (response?.isFormDisabled && callbacks.shouldApplyFormDisabled())
   {
      const message = response.formDisabledMessage ?? "You are not allowed to edit this record.";
      callbacks.onFormDisabled(message);
      callbacks.disableAllFields();
   }
}


/***************************************************************************
 * Run an on-change form adjuster for a single field. Returns a promise that
 * resolves when the backend round-trip completes and the response has been
 * applied.
 *
 * `formFields` is passed by reference so this function can mutate field
 * metadata in place (for `updatedFieldMetaData`, temporary isEditable
 * flipping, etc.). Caller is responsible for triggering a re-render via
 * `onFormFieldsChanged()` after this resolves (or on interim mutations).
 ***************************************************************************/
export interface RunOnChangeFormAdjusterArgs
{
   tableName: string;
   table: QTableMetaData;
   fieldName: string;
   newValue: any;
   materialDashboardFieldMetaData: any;
   formFields: { [fieldName: string]: DynamicFormFieldDefinition };
   formValuesRef: React.MutableRefObject<{ [fieldName: string]: any }>;
   setFieldValueRef: React.MutableRefObject<((name: string, value: any, shouldValidate?: boolean) => void) | null>;
   onFormFieldsChanged: () => void;
   callbacks: FormAdjusterCallbacks;
   /** Optional predicate checked after the network round-trip; when it
    *  returns false the response is dropped before any state is touched.
    *  See `runOnLoadFormAdjuster` for the row-edit motivation. */
   isStillCurrent?: () => boolean;
   /** Optional — called once per field replaced via `updatedFieldMetaData`,
    *  after formFields and table.fields have been updated.  Hosts use this to
    *  rebuild that field's validation: the Yup schema is derived from field
    *  metadata when the form loads and does not track later formFields
    *  mutations, so without this an adjuster-driven isRequired change never
    *  reaches validation (e.g. a hidden-but-required field silently blocking
    *  submit). */
   onFieldMetaDataReplaced?: (fieldName: string, updatedField: QFieldMetaData) => void;
   /** Optional — the host's statically-disabled fields, applied when rebuilding
    *  a replaced field's definition, so a disabled field stays non-editable and
    *  non-required after an adjuster replaces its metadata (matching how
    *  getFormData applies them at load time). */
   disabledFields?: DisabledFields;
}

export async function runOnChangeFormAdjuster(args: RunOnChangeFormAdjusterArgs): Promise<void>
{
   const {tableName, table, fieldName, newValue, materialDashboardFieldMetaData, formFields, formValuesRef, setFieldValueRef, onFormFieldsChanged, callbacks, isStillCurrent, onFieldMetaDataReplaced, disabledFields} = args;

   const field = formFields[fieldName];
   if (!field) return;

   const adjuster = materialDashboardFieldMetaData.onChangeFormAdjuster;
   if (!adjuster) return;

   const fieldNamesToTempDisable: string[] = materialDashboardFieldMetaData.fieldsToDisableWhileRunningAdjusters ?? [];
   const previousIsEditableValues: { [key: string]: boolean } = {};
   if (fieldNamesToTempDisable.length > 0)
   {
      for (let fn in formFields)
      {
         if (fieldNamesToTempDisable.includes(fn))
         {
            previousIsEditableValues[fn] = formFields[fn].isEditable;
            formFields[fn].isEditable = false;
         }
      }
      onFormFieldsChanged();
   }

   ////////////////////////////////////////////////////////////////////////////////
   // Track which fields the backend response replaces via updatedFieldMetaData. //
   // The finally block uses this to skip restoring previousIsEditableValues for //
   // a field the backend just intentionally re-set — otherwise a temp-disable   //
   // restore would clobber the backend's new editability.                       //
   ////////////////////////////////////////////////////////////////////////////////
   const fieldsReplacedByResponse = new Set<string>();

   try
   {
      const postBody = new FormData();
      postBody.append("event", "onChange");
      postBody.append("fieldName", fieldName);
      postBody.append("newValue", newValue);
      postBody.append("allValues", JSON.stringify(formValuesRef.current));
      const response: FormAdjusterOutput = await qController.axiosRequest(
         {
            method: "post",
            url: `/material-dashboard-backend/form-adjuster/${encodeURIComponent(materialDashboardFieldMetaData.formAdjusterIdentifier)}/onChange`,
            data: postBody,
            headers: qController.defaultMultipartFormDataHeaders()
         });

      //////////////////////////////////////////////////////////////////////////////
      // If the calling host moved on (e.g. row-edit cancelled or switched to a   //
      // different row) drop this response before touching any shared state.  The //
      // finally block below still runs to restore temp-disabled fields on the    //
      // (now-stale) formFields object — harmless, just unwound work.             //
      //////////////////////////////////////////////////////////////////////////////
      if (isStillCurrent && !isStillCurrent())
      {
         return;
      }

      const updatedFields = response.updatedFieldMetaData;
      if (updatedFields)
      {
         for (let updatedFieldName in updatedFields)
         {
            const updatedField = new QFieldMetaData(updatedFields[updatedFieldName]);
            const dynamicField = DynamicFormUtils.getDynamicField(updatedField, disabledFields);
            const dynamicFieldInObject: Record<string, DynamicFormFieldDefinition> = {};
            dynamicFieldInObject[updatedFieldName] = dynamicField;
            DynamicFormUtils.addPossibleValueProps(dynamicFieldInObject, [updatedFields[updatedFieldName]], tableName, null, new Map());
            formFields[updatedFieldName] = dynamicField;
            table?.fields?.set(updatedFieldName, updatedField);
            fieldsReplacedByResponse.add(updatedFieldName);
            onFieldMetaDataReplaced?.(updatedFieldName, updatedField);
         }
      }

      const updatedFieldValues = response?.updatedFieldValues ?? {};
      for (let fn in updatedFieldValues)
      {
         setFieldValueRef.current?.(fn, updatedFieldValues[fn]);

         /////////////////////////////////////////////////////////////////////////////////
         // mirror the new id onto possibleValueProps.initialValue so the select's      //
         // remount key (which keys on initialValue + initialDisplayValue) changes when //
         // an adjuster switches to a different possible value — even if the new value  //
         // happens to share the same display label as the old one.                     //
         /////////////////////////////////////////////////////////////////////////////////
         const fieldToUpdate = formFields[fn];
         if (fieldToUpdate?.possibleValueProps)
         {
            fieldToUpdate.possibleValueProps.initialValue = updatedFieldValues[fn];
         }
      }

      ///////////////////////////////////////////////////////////////////////////////////
      // Apply updated display values to the (possibly just-rebuilt) possible-value    //
      // fields BEFORE notifying that the form fields changed.  If we notified first   //
      // (as the updatedFieldMetaData rebuild used to), the change-notification would  //
      // snapshot the field without its display value — and a field the adjuster also  //
      // disabled would render its read-only view from a stale object, falling back to //
      // showing the raw id instead of the display value (label).                      //
      ///////////////////////////////////////////////////////////////////////////////////
      const updatedFieldDisplayValues = response?.updatedFieldDisplayValues ?? {};
      let anyDisplayValueApplied = false;
      for (let fn in updatedFieldDisplayValues)
      {
         const fieldToUpdate = formFields[fn];
         if (fieldToUpdate?.possibleValueProps)
         {
            fieldToUpdate.possibleValueProps.initialDisplayValue = updatedFieldDisplayValues[fn];
            anyDisplayValueApplied = true;
         }
      }

      if (updatedFields || anyDisplayValueApplied)
      {
         onFormFieldsChanged();
      }

      const fieldsToClear = response?.fieldsToClear ?? [];
      for (let fn of fieldsToClear)
      {
         setFieldValueRef.current?.(fn, "");
      }

      callbacks.applyUpdatedSections(response);

      if (response?.isFormDisabled)
      {
         const message = response.formDisabledMessage ?? "You are not allowed to edit this record.";
         callbacks.onFormDisabled(message);
         callbacks.disableAllFields();
      }
   }
   finally
   {
      if (fieldNamesToTempDisable.length > 0)
      {
         for (let fn in formFields)
         {
            if (fieldNamesToTempDisable.includes(fn) && !fieldsReplacedByResponse.has(fn))
            {
               formFields[fn].isEditable = previousIsEditableValues[fn];
            }
         }
         onFormFieldsChanged();
      }
   }
}


/***************************************************************************
 * Evaluate ON_CHANGE field rules for a changed field. Populates
 * `valueChangesToMake` (which the caller applies via setFieldValue) and
 * calls `reloadWidget` for `RELOAD_WIDGET` rules.
 ***************************************************************************/
export function evaluateFieldRules(
   fieldRules: FieldRule[],
   fieldName: string,
   newValue: any,
   valueChangesToMake: { [fieldName: string]: any },
   reloadWidget: (widgetName: string, additionalParams?: { [key: string]: any }) => void
): void
{
   for (let fieldRule of fieldRules)
   {
      if (fieldRule.trigger === FieldRuleTrigger.ON_CHANGE && fieldRule.sourceField === fieldName)
      {
         switch (fieldRule.action)
         {
            case FieldRuleAction.CLEAR_TARGET_FIELD:
               valueChangesToMake[fieldRule.targetField] = null;
               break;
            case FieldRuleAction.RELOAD_WIDGET:
               const additionalParams: { [key: string]: any } = {};
               if (newValue != null)
               {
                  additionalParams[fieldRule.sourceField] = newValue;
               }
               reloadWidget(fieldRule.targetWidget, additionalParams);
               break;
         }
      }
   }
}


/***************************************************************************
 * Renderless Formik child that watches form values and fires an
 * `onFieldChange` callback for each changed field. Uses a JSON-diff against
 * a caller-owned ref rather than Formik's `dirty` flag, because field rules
 * must fire on every value transition — including a transition back to the
 * initial value — to reverse any side effects caused by intermediate states.
 *
 * Caller responsibilities:
 * - Provide `formValuesRef` (a mutable ref starting at `{}`). The tracker
 *   mutates this to reflect the latest per-field values.
 * - Provide `formValuesJSONRef` (a mutable ref starting at `""`). The tracker
 *   maintains the last-seen JSON snapshot here.
 * - Provide `setFieldValueRef` (a mutable ref starting at `null`). The tracker
 *   stores Formik's `setFieldValue` here so async callers (adjusters, field
 *   rules) can use it outside of render.
 * - Provide `onFieldChange(fieldName, oldValue, newValue, valueChangesToMake)`
 *   — the same signature RecordScreen's `handleFieldChange` uses. The tracker
 *   applies `valueChangesToMake` via `setFieldValue(name, value, false)`
 *   after the callback returns.
 ***************************************************************************/
export interface FormikValueTrackerProps
{
   formValuesRef: React.MutableRefObject<{ [fieldName: string]: any }>;
   formValuesJSONRef: React.MutableRefObject<string>;
   setFieldValueRef: React.MutableRefObject<((name: string, value: any, shouldValidate?: boolean) => void) | null>;
   onFieldChange: (fieldName: string, oldValue: any, newValue: any, valueChangesToMake: { [fieldName: string]: any }) => void;
}

export const FormikValueTracker: React.FC<FormikValueTrackerProps> = ({formValuesRef, formValuesJSONRef, setFieldValueRef, onFieldChange}) =>
{
   const {values, setFieldValue} = useFormikContext<any>();

   setFieldValueRef.current = setFieldValue;

   if (values)
   {
      const newJSON = JSON.stringify(values);
      if (formValuesJSONRef.current !== newJSON)
      {
         const valueChangesToMake: { [fieldName: string]: any } = {};

         ///////////////////////////////////////////////////////////////////////////////
         // only fire onFieldChange once a baseline has been established (i.e. not on //
         // the very first time we observe values).  formValuesJSONRef starts at ""   //
         // and is set below after the first pass — using it (rather than "is         //
         // formValuesRef non-empty?") is what makes this correct when the initial    //
         // values are empty (e.g. a create screen): otherwise formValuesRef stays    //
         // empty through the baseline and the FIRST real change is silently          //
         // swallowed (its on-change form adjuster never runs).                       //
         ///////////////////////////////////////////////////////////////////////////////
         const hasEstablishedBaseline = formValuesJSONRef.current !== "";
         for (let fieldName in values)
         {
            if (hasEstablishedBaseline && formValuesRef.current[fieldName] !== values[fieldName])
            {
               onFieldChange(fieldName, formValuesRef.current[fieldName], values[fieldName], valueChangesToMake);
            }
            formValuesRef.current[fieldName] = values[fieldName];
         }

         for (let fieldName in valueChangesToMake)
         {
            formValuesRef.current[fieldName] = valueChangesToMake[fieldName];
            setFieldValue(fieldName, valueChangesToMake[fieldName], false);
         }

         formValuesJSONRef.current = JSON.stringify(values);
      }
   }

   return null;
};
